import React, { useState, useMemo, useId } from "react";
import { MdExpandMore } from "react-icons/md";
import { FaHeartbeat } from "react-icons/fa";
import clsx from "clsx";
import { MenuItem } from "../../redux/slices/menuSlice";

interface NutritionalInfoProps {
  product: MenuItem;
}

const NutritionalInfo: React.FC<NutritionalInfoProps> = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();

  /**
   * Scalable nutrients array
   * Add more nutrients here without touching JSX
   */
  const nutrients = useMemo(
    () =>
      [
        product.Calories && { label: "Calories", value: product.Calories },
        product.Nutrients && { label: "Nutrients", value: product.Nutrients },
      ].filter(Boolean) as { label: string; value: string | number }[],
    [product.Calories, product.Nutrients]
  );

  if (nutrients.length === 0) return null;

  return (
    <section
      aria-labelledby={`${contentId}-title`}
      className="mb-6 sm:mb-8 rounded-lg border border-gray-200 bg-white overflow-hidden"
    >
      {/* Toggle Button */}
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setIsExpanded((prev) => !prev)}
        className={clsx(
          "w-full flex items-center justify-between text-left",
          "px-3 sm:px-4 py-2.5 sm:py-3",
          "bg-gray-50 hover:bg-gray-100 transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
        )}
      >
        <h2
          id={`${contentId}-title`}
          className="flex items-center gap-2 text-sm sm:text-base md:text-lg font-semibold"
        >
          <FaHeartbeat className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 shrink-0" />
          Nutritional Information
        </h2>

        <MdExpandMore
          aria-hidden
          className={clsx(
            "h-5 w-5 text-gray-500",
            "transition-transform duration-300 ease-in-out",
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {/* Animated Content */}
      <div
        id={contentId}
        role="region"
        className={clsx(
          "flex flex-wrap min-w-0 px-3 sm:px-4",
          "gap-2",
          "transition-[max-height,opacity,padding] duration-300 ease-in-out",
          isExpanded
            ? "max-h-96 py-3 sm:py-4 opacity-100"
            : "max-h-0 py-0 opacity-0 overflow-hidden"
        )}
      >
        {nutrients.map(({ label, value }) => (
          <NutrientBadge key={label} label={label} value={value} />
        ))}
      </div>
    </section>
  );
};

interface NutrientBadgeProps {
  label: string;
  value: string | number;
}

const NutrientBadge: React.FC<NutrientBadgeProps> = React.memo(
  ({ label, value }) => (
    <span
      title={`${label}: ${value}`} // ✅ full text on hover
      className={clsx(
        "inline-flex items-center justify-center",
        "max-w-full min-w-0 rounded-full bg-gray-100",
        "px-2.5 sm:px-3 py-1",
        "text-[11px] sm:text-xs md:text-sm",
        "font-medium text-gray-700"
      )}
    >
      <span className="truncate line-clamp-1 sm:line-clamp-2">
        {label}: {value}
      </span>
    </span>
  )
);

NutrientBadge.displayName = "NutrientBadge";

export default NutritionalInfo;
